'use client'

import React, { useEffect, useState } from 'react'
import { Flex } from '@mantine/core'
import { usePathname } from 'next/navigation'
import { useUserContext } from '@/contexts/UserContext'
import { useBreakpoint } from '@/hooks/useBreakpoints'
import { isAnySignupPath } from '@/lib/namedRoutes'
import MenuDrawer from '@/components/NavBar/MenuDrawer'
import { HeaderActions } from '@/components/NavBar/NavBarHeader/HeaderActions'
import { Logo } from '@/components/NavBar/NavBarHeader/Logo'
import { getNavTheme } from '@/components/NavBar/NavBarHeader/theme'

interface NavBarHeaderProps {
    setShowSearchDrawer: (value: boolean) => void
    shouldShowPrimaryColors?: boolean
    isFromGenAi?: boolean
    logoOnly?: boolean
}

const NavBarHeader: React.FC<NavBarHeaderProps> = ({
    setShowSearchDrawer,
    shouldShowPrimaryColors = false,
    isFromGenAi = false,
    logoOnly = false,
}) => {
    const pathname = usePathname()
    const { isSm } = useBreakpoint()
    const { fetchUser, user } = useUserContext()
    const [hasFetchedUser, setHasFetchedUser] = useState(false)
    const [showMenuDrawer, setShowMenuDrawer] = useState(false)

    const isLoggedIn = !!user
    const theme = getNavTheme(shouldShowPrimaryColors || isFromGenAi)

    useEffect(() => {
        if (!user) {
            fetchUser()?.finally(() => setHasFetchedUser(true))
        } else {
            setHasFetchedUser(true)
        }
    }, [user, fetchUser])

    return (
        <Flex
            justify="space-between"
            align="center"
            py={24}
            px={isSm ? 24 : 32}
            w="100%"
            style={{ zIndex: 1001, position: 'relative' }}
            gap="lg"
        >
            <Logo pathname={pathname} isFromGenAi={isFromGenAi} />

            {!logoOnly && !isAnySignupPath(pathname) && (
                <Flex flex={1} justify="flex-end" align="center">
                    <HeaderActions
                        pathname={pathname}
                        isLoggedIn={isLoggedIn}
                        hasFetchedUser={hasFetchedUser}
                        isBlocked={!!user?.blocked}
                        theme={theme}
                        onOpenSearch={() => setShowSearchDrawer(true)}
                        onOpenMenu={() => setShowMenuDrawer(true)}
                    />
                </Flex>
            )}

            {!logoOnly && (
                <MenuDrawer
                    isLoggedIn={isLoggedIn}
                    showMenuDrawer={showMenuDrawer}
                    setShowMenuDrawer={setShowMenuDrawer}
                />
            )}
        </Flex>
    )
}

export default NavBarHeader
